import { useMemo } from "react";

import { useLocale } from "@/i18n";
import { useCurrencyContext } from "./provider";
import { useExchangeRates } from "./use-exchange-rates";
import { CURRENCIES, CURRENCY_SYMBOL, ZERO_DECIMAL_CURRENCIES, type CurrencyCode } from "./config";

export type { CurrencyCode };
export { CURRENCIES, CURRENCY_SYMBOL };

/** Converts a GBP pence amount into major units of `code`, using GBP-based rates. */
export function convertPenceToCurrency(pence: number, code: CurrencyCode, rates: Record<CurrencyCode, number>): number {
  const rate = rates[code] ?? 1;
  return (pence / 100) * rate;
}

export function formatCurrencyAmount(amount: number, code: CurrencyCode, locale: string): string {
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has(code);
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits: zeroDecimal ? 0 : amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: zeroDecimal ? 0 : 2,
  }).format(amount);
}

/**
 * Reads/sets the shopper's display currency and exposes conversion helpers.
 * GBP remains the sole currency of record for bidding/checkout/Stripe — this
 * only affects how amounts are *shown*.
 */
export function useCurrency() {
  const { currency, setCurrency } = useCurrencyContext();
  const rates = useExchangeRates();
  const locale = useLocale();

  return useMemo(() => {
    const formatGBP = (pence: number) => formatCurrencyAmount((pence ?? 0) / 100, "GBP", locale);
    const convert = (pence: number, code: CurrencyCode = currency) =>
      convertPenceToCurrency(pence ?? 0, code, rates.rates);
    const formatConverted = (pence: number, code: CurrencyCode = currency) =>
      formatCurrencyAmount(convert(pence, code), code, locale);
    return {
      currency,
      setCurrency,
      locale,
      rates: rates.rates,
      ratesSource: rates.source,
      isGBP: currency === "GBP",
      formatGBP,
      convert,
      formatConverted,
    };
  }, [currency, setCurrency, locale, rates]);
}
